import { AUTO_STYLE, keyframes, style } from '@angular/animations';
import { Directive, ElementRef, Input, OnInit } from '@angular/core';

import { EnumNgEventName } from '../../shared/event-animation.data';
import { NgContinuousAnimationService } from '../../shared/services/continuous-animation.service';
import { INgAnimationStates, INgAnimationTiming, INgEffectOptions } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function hang(translateY: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	translateY = translateY ? translateY : 8;

	//1 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility 	: AUTO_STYLE,
				transform 	: `translateY(0)`,
				easing 		: 'ease',
				offset 		: 0
			}),
			style({
				transform 	: `translateY(${translateY / 2}px)`,
				easing 		: 'ease',
				offset 		: 0.25
			}),
			style({
				transform 	: `translateY(${translateY}px)`,
				easing 		: 'ease',
				offset 		: 0.5
			}),
			style({
				transform 	: `translateY(${translateY / 2}px)`,
				easing 		: 'ease',
				offset 		: 0.75
			}),
			style({
				transform 	: `translateY(0)`,
				easing 		: 'ease',
				offset 		: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * Hang on Event
 */
@Directive({
    selector: '[ngHangOnEvent]',
    providers: [NgContinuousAnimationService],
    standalone: false
})
export class NgHangOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomEffectOptions> = null!;
	@Input() targetElement: HTMLElement = null!;

	constructor(
		private elementRef					: ElementRef,
		private continuousAnimationService 	: NgContinuousAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translateY value is relative to the animation functionality
		if (this.effectOptions?.translateY && this.effectOptions?.translateY < 0) throw new Error(`HangOnEventDirective => ngOnInit => FATAL ERROR: Provided translateY value should be a positive number: ${this.effectOptions?.translateY}`);

		//1 - setup the animation
		this.continuousAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: hang(this.effectOptions?.translateY),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay
			}
		});
	}
}


/**------------------------------------------------------
 * Hang on Mouse Hover
 */
@Directive({
    selector: '[ngHangOnMouseHover]',
    providers: [NgContinuousAnimationService],
    standalone: false
})
export class NgHangOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() timingOptions: Partial<INgCustomAnimationTiming> = null!;
	@Input() targetElement: HTMLElement 					= null!;

	constructor(
		private elementRef					: ElementRef,
		private continuousAnimationService 	: NgContinuousAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translateY value is relative to the animation functionality
		if (this.timingOptions?.translateY && this.timingOptions?.translateY < 0) throw new Error(`HangOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided translateY value should be a positive number: ${this.timingOptions?.translateY}`);

		//1 - setup the animation
		this.continuousAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: hang(this.timingOptions?.translateY),
			event			: EnumNgEventName.MouseHover,
			timing			: this.timingOptions
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomEffectOptions extends INgEffectOptions {
	translateY : number;
}

interface INgCustomAnimationTiming extends INgAnimationTiming {
	translateY : number;
}
